import { Injectable } from '@angular/core';
import {
  Event as RouterEvent,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  ResolveEnd,
  ResolveStart,
  Router,
  RoutesRecognized,
} from '@angular/router';
import { ResolvedLoaderPolicy } from '../models/loader-policy.models';
import { LoaderStartConfig, LoaderToken } from '../models/loading.models';
import { LoaderOrchestratorService } from './loader-orchestrator.service';
import { LoaderPolicyService } from './loader-policy.service';

interface RouteNavigationLoaderState {
  navigationPolicySignature?: string;
  navigationToken?: LoaderToken;
  resolvePolicySignature?: string;
  resolveToken?: LoaderToken;
}

type RouteLoaderPhase = 'navigation' | 'resolve';

@Injectable({
  providedIn: 'root',
})
export class RouteLoaderService {
  private readonly navigationLoaderStatesById = new Map<
    number,
    RouteNavigationLoaderState
  >();

  constructor(
    private readonly router: Router,
    private readonly loaderOrchestratorService: LoaderOrchestratorService,
    private readonly loaderPolicyService: LoaderPolicyService
  ) {
    this.router.events.subscribe((routerEvent) =>
      this.handleRouterEvent(routerEvent)
    );
  }

  private handleRouterEvent(routerEvent: RouterEvent): void {
    if (routerEvent instanceof NavigationStart) {
      this.handleNavigationStart(routerEvent);
      return;
    }

    if (routerEvent instanceof RoutesRecognized) {
      this.handleRoutesRecognized(routerEvent);
      return;
    }

    if (routerEvent instanceof ResolveStart) {
      this.handleResolveStart(routerEvent);
      return;
    }

    if (routerEvent instanceof ResolveEnd) {
      this.handleResolveEnd(routerEvent);
      return;
    }

    if (
      routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError
    ) {
      this.cleanupNavigationLoaders(routerEvent.id);
    }
  }

  private handleNavigationStart(navigationStart: NavigationStart): void {
    const navigationLoaderState = this.getOrCreateNavigationLoaderState(
      navigationStart.id
    );
    const resolvedRoutePolicy = this.loaderPolicyService.resolveRoutePolicy(null, {
      owner: { type: 'system', name: 'RouteLoaderService' },
      routeKey: navigationStart.url,
      source: 'route',
    });

    this.applyResolvedPolicy(
      navigationStart.id,
      navigationLoaderState,
      resolvedRoutePolicy,
      'navigation'
    );
  }

  private handleRoutesRecognized(routesRecognized: RoutesRecognized): void {
    const navigationLoaderState = this.getOrCreateNavigationLoaderState(
      routesRecognized.id
    );
    const resolvedRoutePolicy = this.loaderPolicyService.resolveRoutePolicy(
      routesRecognized.state.root,
      {
        owner: { type: 'system', name: 'RouteLoaderService' },
        routeKey: routesRecognized.urlAfterRedirects,
        source: 'route',
      }
    );

    this.applyResolvedPolicy(
      routesRecognized.id,
      navigationLoaderState,
      resolvedRoutePolicy,
      'navigation'
    );
  }

  private handleResolveStart(resolveStart: ResolveStart): void {
    const navigationLoaderState = this.getOrCreateNavigationLoaderState(
      resolveStart.id
    );
    const resolvedRoutePolicy = this.loaderPolicyService.resolveRoutePolicy(
      resolveStart.state.root,
      {
        owner: { type: 'resolver', name: 'RouteLoaderService' },
        routeKey: resolveStart.urlAfterRedirects,
        source: 'resolver',
      }
    );

    this.applyResolvedPolicy(
      resolveStart.id,
      navigationLoaderState,
      resolvedRoutePolicy,
      'resolve'
    );
  }

  private handleResolveEnd(resolveEnd: ResolveEnd): void {
    const navigationLoaderState = this.navigationLoaderStatesById.get(resolveEnd.id);

    if (!navigationLoaderState?.resolveToken) {
      return;
    }

    this.loaderOrchestratorService.stop(navigationLoaderState.resolveToken);
    navigationLoaderState.resolveToken = undefined;
    navigationLoaderState.resolvePolicySignature = undefined;
  }

  private applyResolvedPolicy(
    navigationId: number,
    navigationLoaderState: RouteNavigationLoaderState,
    resolvedLoaderPolicy: ResolvedLoaderPolicy,
    routeLoaderPhase: RouteLoaderPhase
  ): void {
    const activePolicySignature = this.buildPolicySignature(resolvedLoaderPolicy);

    if (
      routeLoaderPhase === 'resolve' &&
      navigationLoaderState.navigationToken &&
      navigationLoaderState.navigationPolicySignature === activePolicySignature
    ) {
      this.stopLoaderForPhase(navigationLoaderState, routeLoaderPhase);
      this.setPolicySignature(navigationLoaderState, routeLoaderPhase, undefined);
      return;
    }

    if (resolvedLoaderPolicy.skip) {
      this.stopLoaderForPhase(navigationLoaderState, routeLoaderPhase);
      this.setPolicySignature(
        navigationLoaderState,
        routeLoaderPhase,
        activePolicySignature
      );
      return;
    }

    if (
      this.getPolicySignature(navigationLoaderState, routeLoaderPhase) ===
        activePolicySignature &&
      this.getLoaderToken(navigationLoaderState, routeLoaderPhase)
    ) {
      return;
    }

    this.stopLoaderForPhase(navigationLoaderState, routeLoaderPhase);

    const loaderToken = this.loaderOrchestratorService.start(
      this.buildLoaderStartConfig(
        navigationId,
        resolvedLoaderPolicy,
        routeLoaderPhase
      )
    );

    this.setLoaderToken(navigationLoaderState, routeLoaderPhase, loaderToken);
    this.setPolicySignature(
      navigationLoaderState,
      routeLoaderPhase,
      activePolicySignature
    );
  }

  private buildLoaderStartConfig(
    navigationId: number,
    resolvedLoaderPolicy: ResolvedLoaderPolicy,
    routeLoaderPhase: RouteLoaderPhase
  ): LoaderStartConfig {
    return {
      mode: resolvedLoaderPolicy.mode,
      scope: resolvedLoaderPolicy.scope,
      owner: resolvedLoaderPolicy.owner,
      blocking: resolvedLoaderPolicy.blocking,
      delayMs: resolvedLoaderPolicy.delayMs,
      groupId:
        resolvedLoaderPolicy.groupId ??
        `route-${routeLoaderPhase}-${navigationId}`,
      message: resolvedLoaderPolicy.message,
      minVisibleMs: resolvedLoaderPolicy.minVisibleMs,
      moduleKey: resolvedLoaderPolicy.moduleKey,
      priority: resolvedLoaderPolicy.priority,
      routeKey: resolvedLoaderPolicy.routeKey,
      source: resolvedLoaderPolicy.source,
      metadata: {
        ...(resolvedLoaderPolicy.metadata ?? {}),
        navigationId,
        routeLoaderPhase,
      },
    };
  }

  private cleanupNavigationLoaders(navigationId: number): void {
    const navigationLoaderState =
      this.navigationLoaderStatesById.get(navigationId);

    if (!navigationLoaderState) {
      return;
    }

    if (navigationLoaderState.resolveToken) {
      this.loaderOrchestratorService.stop(navigationLoaderState.resolveToken);
    }

    if (navigationLoaderState.navigationToken) {
      this.loaderOrchestratorService.stop(navigationLoaderState.navigationToken);
    }

    this.navigationLoaderStatesById.delete(navigationId);
  }

  private stopLoaderForPhase(
    navigationLoaderState: RouteNavigationLoaderState,
    routeLoaderPhase: RouteLoaderPhase
  ): void {
    const loaderToken = this.getLoaderToken(navigationLoaderState, routeLoaderPhase);

    if (!loaderToken) {
      return;
    }

    this.loaderOrchestratorService.stop(loaderToken);
    this.setLoaderToken(navigationLoaderState, routeLoaderPhase, undefined);
  }

  private getOrCreateNavigationLoaderState(
    navigationId: number
  ): RouteNavigationLoaderState {
    const existingNavigationLoaderState =
      this.navigationLoaderStatesById.get(navigationId);

    if (existingNavigationLoaderState) {
      return existingNavigationLoaderState;
    }

    const newNavigationLoaderState: RouteNavigationLoaderState = {};
    this.navigationLoaderStatesById.set(navigationId, newNavigationLoaderState);

    return newNavigationLoaderState;
  }

  private buildPolicySignature(
    resolvedLoaderPolicy: ResolvedLoaderPolicy
  ): string {
    return JSON.stringify({
      blocking: resolvedLoaderPolicy.blocking,
      delayMs: resolvedLoaderPolicy.delayMs,
      groupId: resolvedLoaderPolicy.groupId,
      message: resolvedLoaderPolicy.message,
      minVisibleMs: resolvedLoaderPolicy.minVisibleMs,
      mode: resolvedLoaderPolicy.mode,
      moduleKey: resolvedLoaderPolicy.moduleKey,
      priority: resolvedLoaderPolicy.priority,
      routeKey: resolvedLoaderPolicy.routeKey,
      scopeKey: resolvedLoaderPolicy.scope.key,
      scopeType: resolvedLoaderPolicy.scope.type,
      skip: resolvedLoaderPolicy.skip,
      source: resolvedLoaderPolicy.source,
    });
  }

  private getLoaderToken(
    navigationLoaderState: RouteNavigationLoaderState,
    routeLoaderPhase: RouteLoaderPhase
  ): LoaderToken | undefined {
    return routeLoaderPhase === 'navigation'
      ? navigationLoaderState.navigationToken
      : navigationLoaderState.resolveToken;
  }

  private setLoaderToken(
    navigationLoaderState: RouteNavigationLoaderState,
    routeLoaderPhase: RouteLoaderPhase,
    loaderToken: LoaderToken | undefined
  ): void {
    if (routeLoaderPhase === 'navigation') {
      navigationLoaderState.navigationToken = loaderToken;
      return;
    }

    navigationLoaderState.resolveToken = loaderToken;
  }

  private getPolicySignature(
    navigationLoaderState: RouteNavigationLoaderState,
    routeLoaderPhase: RouteLoaderPhase
  ): string | undefined {
    return routeLoaderPhase === 'navigation'
      ? navigationLoaderState.navigationPolicySignature
      : navigationLoaderState.resolvePolicySignature;
  }

  private setPolicySignature(
    navigationLoaderState: RouteNavigationLoaderState,
    routeLoaderPhase: RouteLoaderPhase,
    policySignature: string | undefined
  ): void {
    if (routeLoaderPhase === 'navigation') {
      navigationLoaderState.navigationPolicySignature = policySignature;
      return;
    }

    navigationLoaderState.resolvePolicySignature = policySignature;
  }
}
